/**
 * RQ-100 — Cadastro, recuperação e reset de senha (pt-BR).
 */
import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Fade,
  Link,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { API_BASE } from "../../api";
import PasswordField from "../../components/common/PasswordField";

async function postJson(path, body) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body)
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = data?.detail;
    const msg = typeof detail === "string" ? detail : detail?.message || data?.message || `Erro HTTP ${res.status}`;
    throw new Error(msg);
  }
  return data;
}

export function RegisterPanel({ onBack }) {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    username: "",
    phone: "",
    password: "",
    password_confirm: ""
  });
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setOk("");
    setBusy(true);
    try {
      const data = await postJson("/api/v1/auth/register", form);
      setOk(data.message || "Cadastro recebido.");
    } catch (err) {
      setError(err.message || "Não foi possível concluir o cadastro.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box component="form" onSubmit={submit}>
      <Typography variant="h5" fontWeight={800} sx={{ mb: 1 }}>
        Faça seu cadastro
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Após o envio, sua conta fica pendente até a aprovação do Super Admin.
      </Typography>
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      {ok ? <Alert severity="success" sx={{ mb: 2 }}>{ok}</Alert> : null}
      <Stack spacing={1.8}>
        <TextField required label="Nome completo" placeholder="Seu nome completo" value={form.full_name} onChange={set("full_name")} fullWidth inputProps={{ "data-testid": "register-fullname" }} />
        <TextField required label="E-mail" type="email" placeholder="voce@empresa.com" value={form.email} onChange={set("email")} fullWidth inputProps={{ "data-testid": "register-email" }} />
        <TextField required label="Usuário" placeholder="usuario.login" value={form.username} onChange={set("username")} fullWidth helperText="Sem espaços" inputProps={{ "data-testid": "register-username" }} />
        <TextField required label="Telefone" placeholder="(11) 99999-0000" value={form.phone} onChange={set("phone")} fullWidth inputProps={{ "data-testid": "register-phone" }} />
        <PasswordField required label="Senha" placeholder="Mín. 8 caracteres, maiúscula e número" value={form.password} onChange={set("password")} fullWidth autoComplete="new-password" inputProps={{ "data-testid": "register-password" }} />
        <PasswordField required label="Confirmar senha" value={form.password_confirm} onChange={set("password_confirm")} fullWidth autoComplete="new-password" inputProps={{ "data-testid": "register-password-confirm" }} />
        <Button type="submit" variant="contained" disabled={busy} sx={{ py: 1.3, fontWeight: 700 }} data-testid="register-submit">
          {busy ? "Enviando…" : "Enviar cadastro"}
        </Button>
        <Button onClick={onBack}>Voltar ao login</Button>
      </Stack>
    </Box>
  );
}

export function ForgotPasswordPanel({ onBack }) {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg("");
    try {
      const data = await postJson("/api/v1/auth/forgot-password", { email });
      setMsg(data.message || "Se o e-mail existir, enviaremos instruções.");
    } catch (err) {
      setMsg(err.message || "Não foi possível enviar. Tente novamente.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box component="form" onSubmit={submit}>
      <Typography variant="h5" fontWeight={800} sx={{ mb: 1 }}>
        Esqueceu sua senha?
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Informe o e-mail da conta. Se estiver cadastrado, enviaremos um link seguro.
      </Typography>
      {msg ? <Alert severity="info" sx={{ mb: 2 }}>{msg}</Alert> : null}
      <Stack spacing={1.8}>
        <TextField required label="E-mail" type="email" placeholder="voce@empresa.com" value={email} onChange={(e) => setEmail(e.target.value)} fullWidth inputProps={{ "data-testid": "forgot-email" }} />
        <Button type="submit" variant="contained" disabled={busy} sx={{ py: 1.3, fontWeight: 700 }} data-testid="forgot-submit">
          {busy ? "Enviando…" : "Enviar link de recuperação"}
        </Button>
        <Button onClick={onBack}>Voltar ao login</Button>
      </Stack>
    </Box>
  );
}

export function ResetPasswordPanel({ token, onBack }) {
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const hasToken = Boolean(token);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setMsg("");
    setBusy(true);
    try {
      const data = await postJson("/api/v1/auth/reset-password", {
        token,
        password,
        password_confirm: passwordConfirm
      });
      setMsg(data.message || "Senha atualizada.");
    } catch (ex) {
      setErr(ex.message || "Não foi possível redefinir a senha.");
    } finally {
      setBusy(false);
    }
  };

  if (!hasToken) {
    return (
      <Alert severity="warning">
        Link incompleto. Use o endereço enviado por e-mail ou solicite nova recuperação.
        <Box sx={{ mt: 1 }}><Button onClick={onBack}>Voltar</Button></Box>
      </Alert>
    );
  }

  return (
    <Box component="form" onSubmit={submit}>
      <Typography variant="h5" fontWeight={800} sx={{ mb: 1 }}>
        Nova senha
      </Typography>
      {err ? <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert> : null}
      {msg ? <Alert severity="success" sx={{ mb: 2 }}>{msg}</Alert> : null}
      <Stack spacing={1.8}>
        <PasswordField required label="Nova senha" value={password} onChange={(e) => setPassword(e.target.value)} fullWidth autoComplete="new-password" />
        <PasswordField required label="Confirmar nova senha" value={passwordConfirm} onChange={(e) => setPasswordConfirm(e.target.value)} fullWidth autoComplete="new-password" />
        <Button type="submit" variant="contained" disabled={busy} sx={{ py: 1.3, fontWeight: 700 }}>
          {busy ? "Salvando…" : "Salvar nova senha"}
        </Button>
        <Button onClick={onBack}>Ir para o login</Button>
      </Stack>
    </Box>
  );
}

export function useAuthHashMode() {
  const [state, setState] = useState(() => {
    if (typeof window === "undefined") return { mode: "login", token: "" };
    const h = window.location.hash || "";
    if (h.includes("reset-password")) {
      const q = h.split("?")[1] || "";
      const token = new URLSearchParams(q).get("token") || "";
      return { mode: "reset", token };
    }
    if (h.includes("register")) return { mode: "register", token: "" };
    if (h.includes("forgot")) return { mode: "forgot", token: "" };
    return { mode: "login", token: "" };
  });

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const read = () => {
      const h = window.location.hash || "";
      if (h.includes("reset-password")) {
        const q = h.split("?")[1] || "";
        const token = new URLSearchParams(q).get("token") || "";
        setState({ mode: "reset", token });
        return;
      }
      if (h.includes("register")) {
        setState({ mode: "register", token: "" });
        return;
      }
      if (h.includes("forgot")) {
        setState({ mode: "forgot", token: "" });
        return;
      }
      setState({ mode: "login", token: "" });
    };
    window.addEventListener("hashchange", read);
    return () => window.removeEventListener("hashchange", read);
  }, []);

  return state;
}

export function AuthShell({ children }) {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        background:
          "radial-gradient(circle at top right, rgba(37,99,235,0.14), transparent 25%), linear-gradient(180deg, #f8fbff 0%, #f3f6fb 100%)"
      }}
    >
      <Container maxWidth="sm">
        <Fade in timeout={450}>
          <Card
            sx={{
              borderRadius: 5,
              boxShadow: "0 18px 50px rgba(15,23,42,0.12)",
              border: "1px solid #e5e7eb"
            }}
          >
            <CardContent sx={{ p: 4.5 }}>{children}</CardContent>
          </Card>
        </Fade>
      </Container>
    </Box>
  );
}

export { Link };
